import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "config/api";

// @mui
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Removed unused MUI Menu imports and language icon imports

// ZionLadder UI
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import QRCodePanel from "./QRCode";
import PayOptions from "./PayOptions";
import CardPayment from "./CardPayment";
import RefreshStatus from "./RefreshStatus";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Navbar from "components/Navbars";

// Removed flag image imports

const DEFAULT_PAYMENT_METHODS = {
  stripe_alipay: true,
  stripe_wechat: true,
  stripe_card: true,
  airpay_alipay: true,
  airpay_wechat: true,
  airpay_unionpay: true,
  airpay_alipay_hk: true,
  epay_wechat: true,
  epay_alipay: true,
};

function normalizePaymentMethods(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    stripe_alipay: Boolean(src.stripe_alipay),
    stripe_wechat: Boolean(src.stripe_wechat),
    stripe_card: Boolean(src.stripe_card),
    airpay_alipay: Boolean(src.airpay_alipay),
    airpay_wechat: Boolean(src.airpay_wechat),
    airpay_unionpay: Boolean(src.airpay_unionpay),
    airpay_alipay_hk: Boolean(src.airpay_alipay_hk),
    epay_wechat: Boolean(src.epay_wechat),
    epay_alipay: Boolean(src.epay_alipay),
  };
}

function getAvailableMethods(cfg) {
  const methods = [];
  if (cfg.stripe_wechat || cfg.airpay_wechat || cfg.epay_wechat) methods.push("wechatpay");
  if (cfg.stripe_alipay || cfg.airpay_alipay || cfg.epay_alipay) methods.push("alipay");
  if (cfg.airpay_alipay_hk) methods.push("alipayhk");
  if (cfg.stripe_card || cfg.airpay_unionpay) methods.push("card");
  return methods;
}

function getAvailableProviders(cfg, method) {
  if (method === "wechatpay") {
    return ["stripe", "airwallex", "epay"].filter((p) => {
      if (p === "stripe") return cfg.stripe_wechat;
      if (p === "airwallex") return cfg.airpay_wechat;
      return cfg.epay_wechat;
    });
  }
  if (method === "alipay") {
    return ["stripe", "airwallex", "epay"].filter((p) => {
      if (p === "stripe") return cfg.stripe_alipay;
      if (p === "airwallex") return cfg.airpay_alipay;
      return cfg.epay_alipay;
    });
  }
  if (method === "card") {
    const providers = [];
    if (cfg.stripe_card) {
      providers.push("stripe");
      providers.push("stripe_checkout");
    }
    if (cfg.airpay_unionpay) providers.push("airwallex");
    return providers;
  }
  if (method === "alipayhk") {
    return cfg.airpay_alipay_hk ? ["airwallex"] : [];
  }
  return [];
}

function Payments() {
  const { t } = useTranslation();

  const [provider, setProvider] = useState("stripe");
  const [method, setMethod] = useState("wechatpay");
  const [paymentMethodsCfg, setPaymentMethodsCfg] = useState(DEFAULT_PAYMENT_METHODS);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  // Removed language menu state

  // Refresh/poll backend status by public_id (NewPayRecord.is_topuped)
  const [refreshEnabled, setRefreshEnabled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Card payment
  const [cardReady, setCardReady] = useState(false);
  const [cardProcessing, setCardProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);

  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const cardElementRef = useRef(null);
  const cardMountRef = useRef(null);

  const location = useLocation();

  const query = useMemo(() => {
    // Preferred: state passed from PricingSection
    if (
      location.state &&
      location.state.shop_pk !== null &&
      location.state.shop_pk !== undefined &&
      location.state.shop_pk !== ""
    ) {
      return location.state;
    }

    // Fallback: sessionStorage (after login redirect or refresh)
    try {
      const raw = sessionStorage.getItem("pending_payment");
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.shop_pk !== null && saved.shop_pk !== undefined && saved.shop_pk !== "")
          return saved;
      }
    } catch (e) {
      // ignore
    }

    return null;
  }, [location.state]);

  const backRoute = query?.return_to || "/userinfo/";
  const isPublicTopupByUser = useMemo(
    () =>
      query?.target_user_pk !== null &&
      query?.target_user_pk !== undefined &&
      query?.target_user_pk !== "" &&
      !!query?.username,
    [query]
  );
  const availableMethods = useMemo(
    () => getAvailableMethods(paymentMethodsCfg),
    [paymentMethodsCfg]
  );
  const availableProviders = useMemo(
    () => getAvailableProviders(paymentMethodsCfg, method),
    [paymentMethodsCfg, method]
  );

  useEffect(() => {
    if (!query) {
      // If user enters /payments without context (state/sessionStorage), go back to pricing.
      // Use full navigation so the #pricing hash works reliably.
      window.location.href = "/userinfo/#pricing";
      return;
    }

    // Clear bridge once we are on the page
    try {
      sessionStorage.removeItem("pending_payment");
    } catch (e) {
      // ignore
    }
  }, [query]);

  useEffect(() => {
    if (!query) return;
    let cancelled = false;

    (async () => {
      setMethodsLoading(true);
      try {
        const url = joinUrl(API_BASE_URL, "/web2/payment-methods/");
        const res = await axios.get(url, { withCredentials: true });
        const cfg = normalizePaymentMethods(res?.data?.payment_methods);
        if (!cancelled) setPaymentMethodsCfg(cfg);
      } catch (e) {
        if (!cancelled) {
          setPaymentMethodsCfg(DEFAULT_PAYMENT_METHODS);
        }
      } finally {
        if (!cancelled) setMethodsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query]);

  useEffect(() => {
    if (methodsLoading) return;
    if (!availableMethods.length) {
      setAvailabilityError(t("payments.noPaymentAction"));
      return;
    }
    if (!availableMethods.includes(method)) {
      setMethod(availableMethods[0]);
      return;
    }
    if (!availableProviders.length) {
      setAvailabilityError(t("payments.noPaymentAction"));
      return;
    }
    if (!availableProviders.includes(provider)) {
      setProvider(availableProviders[0]);
      return;
    }
    setAvailabilityError("");
  }, [methodsLoading, availableMethods, availableProviders, method, provider, t]);

  if (!query) {
    return (
      <MKBox component="section" py={{ xs: 6, md: 10 }}>
        <Container>
          <MKTypography variant="body2" color="text">
            {t("payments.loading")}
          </MKTypography>
        </Container>
      </MKBox>
    );
  }

  const getCookie = (name) => {
    if (typeof document === "undefined") return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const joinUrl = (base, path) => {
    const b = String(base || "").replace(/\/$/, "");
    const p = String(path || "").startsWith("/") ? path : `/${path}`;
    return `${b}${p}`;
  };

  // Removed language menu handlers

  const resetPay = () => {
    setError(null);
    setPaymentData(null);

    setRefreshEnabled(false);
    setShowSuccess(false);
    setSuccessData(null);

    setCardReady(false);
    setCardProcessing(false);
    setCardError(null);

    try {
      if (cardElementRef.current) {
        cardElementRef.current.unmount();
        cardElementRef.current = null;
      }
      elementsRef.current = null;
      stripeRef.current = null;
    } catch (e) {
      // ignore
    }
  };

  const resetPaymentPage = () => {
    const nextMethod = availableMethods[0] || "wechatpay";
    const nextProviders = getAvailableProviders(paymentMethodsCfg, nextMethod);

    resetPay();
    setMethod(nextMethod);
    setProvider(nextProviders[0] || "");
    setAvailabilityError("");
  };

  const loadStripeJs = () => {
    if (typeof window === "undefined") return Promise.reject(new Error("No window"));
    if (window.Stripe) return Promise.resolve(window.Stripe);

    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
      if (existing) {
        existing.addEventListener("load", () => resolve(window.Stripe));
        existing.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")));
        return;
      }

      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.async = true;
      script.onload = () => resolve(window.Stripe);
      script.onerror = () => reject(new Error("Failed to load Stripe.js"));
      document.head.appendChild(script);
    });
  };

  const waitForCardMountNode = async (maxTries = 20, delayMs = 50) => {
    for (let i = 0; i < maxTries; i += 1) {
      if (cardMountRef.current) return;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delayMs));
    }
    throw new Error("Card mount node not ready");
  };

  const initCardElement = async (data) => {
    // Cleanup any previous card element before creating a new one
    try {
      if (cardElementRef.current) {
        cardElementRef.current.unmount();
        cardElementRef.current = null;
      }
      if (cardMountRef.current) {
        // Ensure the container is empty before mounting
        cardMountRef.current.innerHTML = "";
      }
    } catch (e) {
      // ignore
    }

    const publicKey = data?.public_key;
    const clientSecret = data?.client_secret;

    if (!publicKey) throw new Error("Missing public_key from backend");
    if (!clientSecret) throw new Error("Missing client_secret from backend");

    setCardError(null);
    setCardReady(false);

    const StripeCtor = await loadStripeJs();
    if (!StripeCtor) throw new Error("Stripe.js not available");

    const stripe = StripeCtor(publicKey);
    stripeRef.current = stripe;

    const elements = stripe.elements();
    elementsRef.current = elements;

    const card = elements.create("card");
    cardElementRef.current = card;

    await waitForCardMountNode();
    card.mount(cardMountRef.current);
    setCardReady(true);

    card.on("change", (evt) => {
      if (evt?.error?.message) setCardError(evt.error.message);
      else setCardError(null);
    });
  };

  const confirmCardPayment = async () => {
    setCardProcessing(true);
    setCardError(null);

    try {
      const stripe = stripeRef.current;
      const cardEl = cardElementRef.current;
      const clientSecret = paymentData?.client_secret;

      if (!stripe) throw new Error("Stripe not initialized");
      if (!cardEl) throw new Error("Card element not ready");
      if (!clientSecret) throw new Error("Missing client_secret");

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardEl,
          billing_details: {
            email: query.username || undefined,
          },
        },
      });

      if (result?.error) throw new Error(result.error.message || "Payment failed");

      // Do not redirect immediately. Backend will confirm via webhook; we poll for is_topuped.
      setRefreshEnabled(true);
    } catch (e) {
      setCardError(e?.message || "Payment failed");
    } finally {
      setCardProcessing(false);
    }
  };

  const startPay = async () => {
    if (!query || !query.shop_pk) {
      setError(t("payments.errors.missingShopPk"));
      return;
    }
    if (methodsLoading) {
      setError(t("common.loading"));
      return;
    }
    if (
      availabilityError ||
      !availableMethods.includes(method) ||
      !availableProviders.includes(provider)
    ) {
      setError(availabilityError || t("payments.noPaymentAction"));
      return;
    }

    setLoading(true);
    setError(null);
    resetPay();

    try {
      const csrftoken = getCookie("csrftoken");
      const url = joinUrl(
        API_BASE_URL,
        isPublicTopupByUser ? "/web2/payment/start-by-user/" : "/web2/payment/start/"
      );
      const payload = {
        provider,
        shop_pk: Number(query.shop_pk),
        method,
        ...(query.username ? { username: query.username } : {}),
        ...(isPublicTopupByUser ? { user_pk: Number(query.target_user_pk) } : {}),
      };
      const headers = {
        "Content-Type": "application/json",
      };
      if (!isPublicTopupByUser && csrftoken) {
        headers["X-CSRFToken"] = csrftoken;
      }

      const res = await axios.post(url, payload, {
        withCredentials: true,
        headers,
      });

      const data = res?.data || {};
      if (!data.ok) throw new Error(data.error || "Payment start failed");

      // Normalize for legacy Stripe-only UI
      // - Stripe returns `payment_method` + (qr_data/redirect_url/client_secret)
      // - Epay/Airpay style may return `mode: 'redirect'|'qrcode'`
      const normalized = { ...data };

      // If backend uses `mode`, map it to current UI expectations.
      if (normalized.mode === "qrcode" && normalized.qrcode) {
        normalized.payment_method = normalized.payment_method || method;
        normalized.qr_data = normalized.qr_data || normalized.qrcode;
      }
      if (normalized.mode === "redirect" && normalized.redirect_url) {
        normalized.payment_method = normalized.payment_method || method;
      }

      setPaymentData(normalized);

      const qrValue =
        normalized.qr_data ||
        normalized.qrcode ||
        normalized.qrcode_url ||
        (normalized.next_action &&
          (normalized.next_action.qrcode_url || normalized.next_action.qrcode)) ||
        "";
      const hasQR = !!(qrValue && String(qrValue).trim());
      const hasRedirect = !!(normalized.redirect_url && String(normalized.redirect_url).trim());

      // Card channel 2: Stripe Checkout (Apple Pay) - redirect via Stripe.js
      if (normalized.payment_method === "card" && provider === "stripe_checkout") {
        const publicKey = normalized.public_key;
        const sessionId = normalized.checkout_session_id;

        if (!publicKey) {
          throw new Error("Missing public_key from backend for Stripe Checkout");
        }
        if (!sessionId) {
          throw new Error("Missing checkout_session_id from backend for Stripe Checkout");
        }

        const StripeCtor = await loadStripeJs();
        if (!StripeCtor) throw new Error("Stripe.js not available");

        const stripe = StripeCtor(publicKey);
        const result = await stripe.redirectToCheckout({ sessionId });
        if (result && result.error) {
          throw new Error(result.error.message || "Failed to redirect to Stripe Checkout");
        }

        // If redirect succeeds, the browser will navigate away.
        return;
      }

      // If backend didn't provide either QR or redirect for a non-card payment, treat as failure.
      if (normalized.payment_method !== "card") {
        if (!hasQR && !hasRedirect) {
          setError(t("payments.noPaymentAction"));
          return;
        }
      }

      // Start polling immediately when we show a QR on this page.
      // Prefer QR when available even if redirect_url is also provided.
      if (hasQR || normalized.mode === "qrcode") {
        setRefreshEnabled(true);
      }

      // For card, start polling only after user clicks "确认付款" and Stripe confirms.
      if (normalized.payment_method === "card") {
        setRefreshEnabled(false);
      }

      if (
        !hasQR &&
        (normalized.payment_method === "alipay" || normalized.mode === "redirect") &&
        normalized.redirect_url
      ) {
        // Prefer QR when available. Only auto-open redirect when there is no QR.
        try {
          window.open(normalized.redirect_url, "_blank", "noopener,noreferrer");
        } catch (e) {
          // Fallback if popup is blocked
          window.location.href = normalized.redirect_url;
          return;
        }

        // Start polling on this page using public_id.
        setRefreshEnabled(true);
        return;
      }

      // Card payment element will be initialized in an effect after render
    } catch (e) {
      setError(e?.message || "Payment request failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!paymentData) return;
    if (paymentData.payment_method !== "card") return;
    // Stripe Checkout card flow does not use Card Element
    if (provider === "stripe_checkout") return;

    let cancelled = false;

    (async () => {
      try {
        setCardError(null);
        setCardReady(false);
        // Wait for the card container to exist in DOM, then init
        await waitForCardMountNode();
        if (cancelled) return;
        await initCardElement(paymentData);
      } catch (e) {
        if (!cancelled) setCardError(e?.message || "Failed to init card payment");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paymentData]);

  useEffect(() => {
    return () => {
      try {
        if (cardElementRef.current) cardElementRef.current.unmount();
      } catch (e) {
        // ignore
      }
    };
  }, []);

  return (
    <>
      <Navbar
        routes={[]}
        action={{
          type: "internal",
          route: backRoute,
          label: t("common.backToHome"),
          color: "info",
        }}
        transparent
        light
      />
      {/* Hero background (needed for transparent navbar) */}
      <MKBox
        minHeight={{ xs: "7rem", md: "7rem" }}
        width="100%"
        sx={{
          position: "relative",
          backgroundImage: "linear-gradient(195deg, rgba(66,66,74,0.9), rgba(25,25,25,0.9))",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      />
      <MKBox component="section" py={{ xs: 6, md: 10 }}>
        <Container>
          <MKBox mb={3}>
            <MKTypography variant="h3" fontWeight="bold">
              {t("payments.title")}
            </MKTypography>
            <MKTypography variant="body2" color="text" mt={1}>
              {t("payments.subtitle")}
            </MKTypography>
            {(query?.duration || query?.total_price) && (
              <MKTypography variant="body2" color="text" mt={1}>
                {query?.duration ? `${t("payments.payOptions.duration")}: ${query.duration}` : null}
                {query?.duration && query?.total_price ? " · " : null}
                {query?.total_price
                  ? `${t("payments.payOptions.total")}: ${query.total_price}`
                  : null}
              </MKTypography>
            )}
            <MKTypography variant="button" color="success" fontWeight="bold" mt={1} display="block">
              {t("payments.notes.currencyConversion")}
            </MKTypography>
          </MKBox>

          <Card sx={{ p: 3, borderRadius: "xl" }}>
            <PayOptions
              provider={provider}
              onProviderChange={(nextProvider) => {
                setProvider(nextProvider);
                resetPay();
              }}
              method={method}
              onMethodChange={(nextMethod) => {
                setMethod(nextMethod);
                resetPay();
              }}
              availableMethods={availableMethods}
              availableProviders={availableProviders}
              methodsLoading={methodsLoading}
              onCancel={resetPaymentPage}
              onContinue={startPay}
              onRetry={startPay}
              loading={loading}
              disabled={cardProcessing || methodsLoading || !!availabilityError}
              isPaymentPending={!!paymentData?.public_id && refreshEnabled}
              retryDisabled={cardProcessing || methodsLoading || !!availabilityError}
              error={availabilityError || error || ""}
              labels={{
                orderTitle: t("payments.payOptions.orderTitle"),
                plan: t("payments.payOptions.plan"),
                duration: t("payments.payOptions.duration"),
                total: t("payments.payOptions.total"),
                methodTitle: t("payments.payOptions.methodTitle"),
                providerTitle: t("payments.payOptions.providerTitle"),
                wechat: t("payments.methods.wechat"),
                alipay: t("payments.methods.alipay"),
                alipayhk: t("payments.methods.alipayhk"),
                card: t("payments.methods.card"),
                cancel: t("common.cancel"),
                continue: t("payments.payOptions.continue"),
                processing: t("payments.payOptions.processing"),
              }}
            />

            {/* QR result (WeChat / Alipay / other providers may all return qr_data) */}
            {!!(paymentData?.qr_data && String(paymentData.qr_data).trim()) &&
              paymentData?.payment_method !== "card" && (
                <QRCodePanel
                  title={
                    paymentData?.payment_method === "alipay" ||
                    paymentData?.payment_method === "alipayhk"
                      ? t("payments.alipay.scan")
                      : t("payments.wechat.scan")
                  }
                  value={paymentData?.qr_data || ""}
                  size={240}
                  hint={t("payments.qr.tip")}
                  linkUrl={paymentData?.hosted_instructions_url || paymentData?.redirect_url || ""}
                  linkText={t("payments.qr.open")}
                />
              )}

            {/* Card result */}
            {paymentData?.payment_method === "card" && provider !== "stripe_checkout" && (
              <CardPayment
                cardMountRef={cardMountRef}
                cardReady={cardReady}
                processing={cardProcessing}
                error={cardError || ""}
                onConfirm={confirmCardPayment}
                labels={{
                  title: t("payments.card.enter"),
                  loading: t("payments.card.loading"),
                  confirm: t("payments.card.pay"),
                  processing: t("common.loading"),
                  hint: t("payments.card.tip"),
                  unionpayHint: t("payments.notes.unionpayNoExpiryCvcHint"),
                }}
              />
            )}

            <RefreshStatus
              publicId={paymentData?.public_id || ""}
              enabled={!!paymentData?.public_id && refreshEnabled}
              intervalMs={10000}
              byUser={isPublicTopupByUser}
              username={query?.username || ""}
              userPk={query?.target_user_pk ?? null}
              onSuccess={(data) => {
                setSuccessData(data);
                setShowSuccess(true);
                setRefreshEnabled(false);
              }}
              labels={{
                title: t("payments.polling"),
                init: t("payments.pollingInit"),
                status: t("payments.pollingStatus"),
                errorPrefix: t("payments.pollingError"),
              }}
            />
          </Card>

          <Dialog open={showSuccess} onClose={() => setShowSuccess(false)} maxWidth="xs" fullWidth>
            <DialogTitle>{t("payments.successTitle")}</DialogTitle>
            <DialogContent>
              <MKTypography variant="body2" color="text">
                {t("payments.successDesc")}
              </MKTypography>
              {successData?.public_id && (
                <MKTypography variant="caption" color="text" display="block" mt={1}>
                  public_id: {successData.public_id}
                </MKTypography>
              )}
            </DialogContent>
            <DialogActions>
              <MKButton
                variant="contained"
                color="success"
                onClick={() => {
                  setShowSuccess(false);
                  window.location.href = query.return_to || "/userinfo/";
                }}
              >
                {t("payments.continueToUserinfo")}
              </MKButton>
            </DialogActions>
          </Dialog>
        </Container>
      </MKBox>
    </>
  );
}

export default Payments;
